/* import __COLOCATED_TEMPLATE__ from './auto-translate.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */
import { action, set } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type AppService from 'embercom/services/app-service';
import type WorkflowLocalizationService from 'embercom/services/workflow-localization-service';
import type Store from '@ember-data/store';

interface Args {
  readOnly?: boolean;
}

export default class AutoTranslate extends Component<Args> {
  @service declare contentEditorService: any;
  @service declare workflowLocalizationService: WorkflowLocalizationService;
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @service declare appService: AppService;
  @service declare store: Store;
  @tracked uiIsEnabled = false;

  constructor(owner: any, args: Args) {
    super(owner, args);
    this.uiIsEnabled = this.workflowLocalizationService.isMultilingualEnabled;

    if (
      this.args.readOnly &&
      this.workflowLocalizationService.isMultilingualEnabled &&
      this.otherLanguages.length > 0
    ) {
      this.workflowLocalizationService.pollForTranslationsInReadonlyMode();
    }
  }

  get isEnabled() {
    return this.uiIsEnabled;
  }

  get languageHeading() {
    return this.args.readOnly
      ? this.intl.t('operator.workflows.visual-builder.ellipsis-menu.show-workflow-in')
      : this.intl.t('operator.workflows.visual-builder.auto-translate-tooltip-edit-mode');
  }

  get otherLanguages() {
    let availableLocales = this.workflowLocalizationService.availableLocalesForEditor;
    let defaultLocaleId = this.workflowLocalizationService.defaultLocaleId;
    return availableLocales.filter((locale) => locale.localeId !== defaultLocaleId);
  }

  get isLoadingLocales() {
    return this.workflowLocalizationService.isLoadingLocales;
  }

  get groupedLanguages() {
    let availableLocales = this.workflowLocalizationService.availableLocalesForEditor;
    let defaultLocaleId = this.workflowLocalizationService.defaultLocaleId;
    let defaultLanguage = availableLocales.find((locale) => locale.localeId === defaultLocaleId);
    let otherLanguages = this.otherLanguages;
    let selectedLocaleId = this.workflowLocalizationService.selectedLocaleId;
    this.workflowLocalizationService.checkLocalizationStatus();
    let isAutoTranslateDisabled = !this.workflowLocalizationService.isMultilingualEnabled;

    return [
      {
        heading: this.languageHeading,
        items:
          this.args.readOnly && !isAutoTranslateDisabled
            ? [
                {
                  text: `${defaultLanguage?.name} ${this.intl.t(
                    'outbound.content-editor.headers.view-mode.workflow-header.default-language',
                  )}`,
                  value: defaultLanguage?.localeId,
                  isLoading: false,
                  isDisabled: false,
                  isSelected: selectedLocaleId === defaultLanguage?.localeId,
                  component: 'content-editor/headers/view-mode/workflow-language-option',
                },
                ...otherLanguages.map((locale) => ({
                  text: locale.name,
                  value: locale.localeId,
                  isLoading: this.workflowLocalizationService.isLoadingLocales,
                  isDisabled: this.workflowLocalizationService.isLoadingLocales,
                  isSelected: selectedLocaleId === locale.localeId,
                  component: 'content-editor/headers/view-mode/workflow-language-option',
                })),
              ]
            : [],
      },
    ];
  }

  noop = () => {};
  @action
  toggleAutoTranslate() {
    if (!this.args.readOnly) {
      let activeObject = this.contentEditorService.activeObject;
      let hasDraftFeature = activeObject.canUseDraftWorkflow();

      if (hasDraftFeature && this.contentEditorService.isEditMode) {
        let draft = activeObject?.draftVisualBuilderObject;
        if (draft) {
          if (!draft.additionalData) {
            set(
              draft,
              'additionalData',
              this.store.createFragment('operator/visual-builder/additional-data'),
            );
          }
          if (!draft.additionalData.customBotData) {
            set(
              draft.additionalData,
              'customBotData',
              this.store.createFragment('operator/custom-bots/custom-bot-data'),
            );
          }
          let current = draft.additionalData.customBotData.multilingualEnabled;
          set(draft.additionalData.customBotData, 'multilingualEnabled', !current);
          this.uiIsEnabled = !this.uiIsEnabled;
        }
      } else if (activeObject?.botData) {
        activeObject.botData.multilingualEnabled = !activeObject.botData.multilingualEnabled;
        this.uiIsEnabled = !this.uiIsEnabled;
      }
    }
  }

  @action
  selectLanguage(language: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'workflow_language_selector',
      language_selected: language,
    });

    this.workflowLocalizationService.selectLanguage(language);
  }
}
declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'content-editor/headers/auto-translate': typeof AutoTranslate;
    'ContentEditor::Headers::AutoTranslate': typeof AutoTranslate;
  }
}
